<script lang="ts">
	import { onMount, onDestroy, tick } from 'svelte';
	import { browser } from '$app/environment';
	import authStore from '$lib/authStore';
	import { Diffuser } from '$lib/diffuser';
	import type { DiffusionJobInfoCreate, DiffusionJobImage } from '$lib/diffusionApi';
	import Header from '$components/Header.svelte';
	import LoginOrSignUpModal from '$components/LoginOrSignUpModal.svelte';
	import UsageExceededModal from '../canvas/UsageExceededModal.svelte';
	import { analytics } from '$lib/analytics';
	import { getApiErrorDetail } from '$lib/getApiErrorDetail';
	import { RippleShader } from '$lib/rippleShader';

	// Constants
	const modelName = 'GPT';
	const canvasWidth = 1024;
	const canvasHeight = 1024;
	// Downscale references before upload: the model downsamples server-side anyway and the
	// output is 1024², so this is lossless in practice but avoids multi-MB uploads / 413s.
	const MAX_UPLOAD_EDGE = 2048;

	// State
	type ToolState = 'upload' | 'ready' | 'generating' | 'done';
	let toolState: ToolState = 'upload';
	let referenceImage: string | undefined = undefined;
	let generatedTexture: string | undefined = undefined;
	let previewImages: string[] = [];
	let errorMessage: string = '';
	let lastPreviewImageNumber = 0;
	let generationId: string | undefined = undefined;

	// Store last successful result for "Back to result" functionality
	let lastSuccessfulTexture: string | undefined = undefined;
	let lastSuccessfulGenerationId: string | undefined = undefined;

	// Timer
	let jobStartTime: number = 0;
	let elapsedTime: number = 0;
	let estimatedWaitTime: number | undefined = undefined;
	let timerInterval: ReturnType<typeof setInterval> | null = null;

	// Diffuser
	let diffuser: Diffuser | undefined = undefined;

	// Ripple shader for generating effect
	let rippleShader: RippleShader | undefined = undefined;
	let rippleAnimationId: number | undefined = undefined;
	let imageDisplayCanvas: HTMLCanvasElement | undefined = undefined;
	let rippleCanvas: HTMLCanvasElement | undefined = undefined;
	let rippleCanvasReady = false;

	// Modals
	let loginOrSignUpModal: LoginOrSignUpModal;
	let usageExceededModal: UsageExceededModal;
	let usageExceededErrorDetail: string = '';

	// Subscriptions
	let subscriptions: (() => void)[] = [];

	onMount(() => {
		if (!browser) return;

		subscriptions.push(
			authStore.subscribe(async (auth) => {
				if (auth.user) {
					const token = (await auth.user.getIdToken()) ?? '';
					if (!diffuser) {
						diffuser = new Diffuser(token);
					} else {
						diffuser.setToken(token);
					}
				}
			})
		);
	});

	onDestroy(() => {
		for (const unsubscribe of subscriptions) {
			unsubscribe();
		}
		if (timerInterval) {
			clearInterval(timerInterval);
		}
		stopRippleAnimation();
	});

	function readFileAsDataUrl(file: File): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = (event) => resolve(event.target?.result as string);
			reader.onerror = () => reject(reader.error);
			reader.readAsDataURL(file);
		});
	}

	// Downscale to MAX_UPLOAD_EDGE on the long edge (aspect preserved, no crop) and re-encode
	// as JPEG to keep the upload small. Returns the original if it's already small enough.
	function downscaleImage(dataUrl: string, maxEdge: number): Promise<string> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.onload = () => {
				const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
				if (scale === 1) {
					resolve(dataUrl);
					return;
				}
				const canvas = document.createElement('canvas');
				canvas.width = Math.round(img.width * scale);
				canvas.height = Math.round(img.height * scale);
				const ctx = canvas.getContext('2d');
				if (!ctx) {
					resolve(dataUrl);
					return;
				}
				ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
				resolve(canvas.toDataURL('image/jpeg', 0.92));
			};
			img.onerror = () => reject(new Error('Could not decode image'));
			img.src = dataUrl;
		});
	}

	async function handleFileSelect(file: File) {
		if (!file || !file.type.startsWith('image/')) {
			errorMessage = 'Please choose an image file.';
			return;
		}

		// If we're on the done state, save the current result before loading new image
		if (toolState === 'done' && generatedTexture) {
			lastSuccessfulTexture = generatedTexture;
			lastSuccessfulGenerationId = generationId;
		}

		try {
			const dataUrl = await readFileAsDataUrl(file);
			referenceImage = await downscaleImage(dataUrl, MAX_UPLOAD_EDGE);
			generatedTexture = undefined;
			previewImages = [];
			errorMessage = '';
			toolState = 'ready';
			analytics.capture('reference_to_texture_image_uploaded');
		} catch (e) {
			console.error('Failed to load image:', e);
			errorMessage = 'Could not load that image. Please try a different file.';
		}
	}

	function openFileUploader() {
		const input = document.createElement('input');
		input.type = 'file';
		input.accept = 'image/*';
		input.onchange = (e) => {
			const file = (e.target as HTMLInputElement).files?.[0];
			if (file) handleFileSelect(file);
		};
		input.click();
	}

	function clearReference() {
		referenceImage = undefined;
		generatedTexture = undefined;
		previewImages = [];
		stopRippleAnimation();
		toolState = 'upload';
	}

	function useToolAgain() {
		// Store the current result before clearing
		if (generatedTexture) {
			lastSuccessfulTexture = generatedTexture;
			lastSuccessfulGenerationId = generationId;
		}
		generatedTexture = undefined;
		previewImages = [];
		toolState = 'ready';
	}

	function backToResult() {
		if (lastSuccessfulTexture) {
			generatedTexture = lastSuccessfulTexture;
			generationId = lastSuccessfulGenerationId;
			toolState = 'done';
		}
	}

	function startTimer() {
		jobStartTime = Date.now();
		elapsedTime = 0;
		timerInterval = setInterval(() => {
			elapsedTime = (Date.now() - jobStartTime) / 1000;
		}, 100);
	}

	function stopTimer() {
		if (timerInterval) {
			clearInterval(timerInterval);
			timerInterval = null;
		}
	}

	function drawRippleFrame(): boolean {
		if (!imageDisplayCanvas || !rippleCanvas) return false;

		if (!rippleShader) {
			rippleShader = new RippleShader(imageDisplayCanvas.width, imageDisplayCanvas.height);
		}
		rippleShader.resize(imageDisplayCanvas.width, imageDisplayCanvas.height);
		rippleShader.draw(imageDisplayCanvas);

		const ctx = rippleCanvas.getContext('2d');
		if (ctx) {
			ctx.clearRect(0, 0, rippleCanvas.width, rippleCanvas.height);
			ctx.drawImage(rippleShader.canvas, 0, 0);
			return true;
		}
		return false;
	}

	function startRippleAnimation() {
		if (!browser) return;

		// Draw first frame immediately and mark canvas as ready
		if (drawRippleFrame()) {
			rippleCanvasReady = true;
		}

		const animate = () => {
			if (toolState !== 'generating') {
				stopRippleAnimation();
				return;
			}

			drawRippleFrame();
			rippleAnimationId = requestAnimationFrame(animate);
		};

		rippleAnimationId = requestAnimationFrame(animate);
	}

	function stopRippleAnimation() {
		if (rippleAnimationId !== undefined) {
			cancelAnimationFrame(rippleAnimationId);
			rippleAnimationId = undefined;
		}
		rippleCanvasReady = false;
	}

	function drawImageToSourceCanvas(imageSrc: string): Promise<void> {
		return new Promise((resolve) => {
			if (!imageDisplayCanvas) {
				resolve();
				return;
			}

			const img = new Image();
			img.onload = () => {
				if (!imageDisplayCanvas) return;
				const ctx = imageDisplayCanvas.getContext('2d');
				if (ctx) {
					// Clear and draw the image centered/fitted
					ctx.clearRect(0, 0, imageDisplayCanvas.width, imageDisplayCanvas.height);

					// Calculate scaling to fit the image while maintaining aspect ratio
					const scale = Math.min(
						imageDisplayCanvas.width / img.width,
						imageDisplayCanvas.height / img.height
					);
					const scaledWidth = img.width * scale;
					const scaledHeight = img.height * scale;
					const x = (imageDisplayCanvas.width - scaledWidth) / 2;
					const y = (imageDisplayCanvas.height - scaledHeight) / 2;

					ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
				}
				resolve();
			};
			img.src = imageSrc;
		});
	}

	async function generateTexture() {
		if (!referenceImage) return;

		const currentUser = $authStore.user;
		if (!currentUser) {
			loginOrSignUpModal.openDialog();
			return;
		}

		if (!diffuser) return;

		analytics.capture('reference_to_texture_generate_clicked');

		toolState = 'generating';
		previewImages = [];
		lastPreviewImageNumber = 0;
		errorMessage = '';
		startTimer();

		// Wait for canvases to mount, draw the image, then start ripple animation
		await tick();
		if (referenceImage) {
			await drawImageToSourceCanvas(referenceImage);
		}
		startRippleAnimation();

		// Extract base64 data from the data URL
		const base64Data = referenceImage.split(';base64,')[1];

		const job: DiffusionJobInfoCreate = {
			job_id: crypto.randomUUID(),
			model_name: modelName,
			text_prompts: ['Make this into a seamless texture.'],
			tiling_mode: true,
			tiling_behavior: 'all',
			seed: Math.floor(Math.random() * 1000000),
			width: canvasWidth,
			height: canvasHeight,
			transform_strength: 1.0,
			color_preserving_noise_percentage: 0.15,
			prompt_addendums: [],
			presets: [],
			preset_user_settings: [],
			prompt_image: base64Data,
			enhance_prompt: false,
			reference_to_texture: true,
			brushed_image: undefined,
			brushed_image_mask: undefined,
			init_latent: undefined,
			inpainting_mask: undefined,
			inpainting_masked_image: undefined,
			brushstroke_count: 0
		};

		diffuser.setJob(job);

		try {
			diffuser.setToken((await currentUser.getIdToken()) ?? '');
			const error = await diffuser.createDiffusionJob();
			estimatedWaitTime = diffuser.estimatedWaitTime;

			if (error) {
				console.error('Error creating diffusion job:', error);
				stopTimer();
				stopRippleAnimation();
				toolState = 'ready';

				const status = (error as any).status;
				if (status === 403) {
					const errorDetail = getApiErrorDetail(error);
					usageExceededErrorDetail = errorDetail;
					analytics.capture('usage_limit_exceeded', { error_detail: errorDetail });
					usageExceededModal.openDialog();
				} else if (status === 413) {
					errorMessage = 'That image is too large to upload. Please try a smaller one.';
				} else {
					errorMessage =
						getApiErrorDetail(error) ||
						'Something went wrong generating your texture. Please try again.';
				}
				return;
			}

			diffuser.streamDiffusionJobImages(
				async (image: DiffusionJobImage) => {
					// The step field is per denoising step; batched variations
					// send one preview per variation per step.
					lastPreviewImageNumber = (image.step ?? 0) + 1;
					previewImages = [...previewImages, image.image_src];

					// Update the source canvas with the latest preview image for the ripple effect
					drawImageToSourceCanvas(image.image_src);

					if (image.is_final_image) {
						generatedTexture = image.image_src;
						generationId = image.generation_id;
						stopTimer();
						stopRippleAnimation();
						toolState = 'done';

						analytics.capture('reference_to_texture_generated', {
							generation_id: image.generation_id,
							generation_time: elapsedTime.toFixed(1)
						});
					}
				},
				async () => {
					if (toolState === 'generating') {
						stopTimer();
						stopRippleAnimation();
						toolState = generatedTexture ? 'done' : 'ready';
					}
				},
				async (e) => {
					console.error('WebSocket error:', e);
					stopTimer();
					stopRippleAnimation();
					toolState = 'ready';
					errorMessage = 'Connection error during generation. Please try again.';
				}
			);
		} catch (e) {
			console.error('Error in generateTexture:', e);
			stopTimer();
			stopRippleAnimation();
			toolState = 'ready';
			errorMessage = 'Something went wrong. Please try again.';
		}
	}

	function downloadTexture() {
		if (!generatedTexture) return;

		const link = document.createElement('a');
		link.href = generatedTexture;
		link.download = 'texture.png';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		analytics.capture('reference_to_texture_downloaded', {
			generation_id: generationId,
			model_name: modelName,
			canvasWidth,
			canvasHeight
		});
	}

	function openInMaterialGenerator() {
		if (!generatedTexture || !browser) return;

		try {
			const materialData = {
				dataUrl: generatedTexture,
				storedAt: Date.now()
			};
			localStorage.setItem('materialGeneratorAlbedo', JSON.stringify(materialData));

			analytics.capture('reference_to_texture_open_material_generator', {
				generation_id: generationId
			});

			window.open('/material-generator', '_blank');
		} catch (error) {
			console.error('Failed to store texture data:', error);
		}
	}

	function handleDrop(e: DragEvent) {
		e.preventDefault();
		if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
			const file = e.dataTransfer.files[0];
			handleFileSelect(file);
		}
	}

	function handleDragOver(e: DragEvent) {
		e.preventDefault();
	}

	// Get the current display image (latest preview or final)
	$: currentDisplayImage =
		generatedTexture ||
		(previewImages.length > 0 ? previewImages[previewImages.length - 1] : undefined);

	// Progress for GPT model (30 steps)
	$: progressPercent = Math.min((lastPreviewImageNumber / 30) * 100, 100);
</script>

<svelte:head>
	<title>Reference to Texture - Iliad</title>
	<meta
		name="description"
		content="Transform any reference image into a seamless tiling texture with Iliad"
	/>
</svelte:head>

<div
	role="presentation"
	on:dragover={handleDragOver}
	on:drop={handleDrop}
	class="min-h-screen w-full bg-primary-25 dark:bg-[#101016]"
>
	<!-- Header -->
	<div class="fixed top-2 left-2 z-20 flex flex-row items-center">
		<Header fixed={true} />
		<div class="w-2"></div>
		<h1 class="px-3 text-xs sm:text-sm font-semibold text-zinc-900 dark:text-zinc-200">
			Reference to Texture
		</h1>
	</div>

	<!-- Main content -->
	<div class="w-full min-h-screen flex flex-col items-center px-4 py-24">
		<!-- Upload / Preview area -->
		<div class="flex flex-col items-center my-auto">
			<p class="text-sm text-zinc-600 dark:text-zinc-400 mb-6 text-center max-w-md">
				Upload a reference image of any surface and transform it into a seamlessly tileable texture
				using AI.
			</p>

			<!-- Upload box / Image preview -->
			<div
				class="relative w-[384px] h-[384px] sm:w-[480px] sm:h-[480px] max-w-[90vw] max-h-[90vw] sm:max-w-none sm:max-h-none rounded-lg
				{toolState === 'upload'
					? 'border-2 border-dashed border-primary-300 dark:border-zinc-700 cursor-pointer hover:border-primary-400 dark:hover:border-zinc-600 bg-primary-50 dark:bg-zinc-900'
					: ''}
				flex flex-col justify-center items-center overflow-hidden"
				on:click={() => toolState === 'upload' && openFileUploader()}
				on:keydown={(e) => e.key === 'Enter' && toolState === 'upload' && openFileUploader()}
				role="button"
				tabindex={toolState === 'upload' ? 0 : -1}
			>
				{#if toolState === 'upload'}
					<!-- Empty upload state -->
					<i class="fa-solid fa-upload text-4xl text-primary-400 dark:text-zinc-600 mb-4"></i>
					<p class="text-sm text-zinc-600 dark:text-zinc-400">Click or drag to upload</p>
				{:else if toolState === 'generating'}
					<!-- During generation: canvases always mounted for binding -->
					<canvas bind:this={imageDisplayCanvas} class="hidden" width="640" height="640"></canvas>
					<!-- Show reference image until ripple canvas is ready -->
					{#if !rippleCanvasReady && referenceImage}
						<img src={referenceImage} alt="Reference" class="w-full h-full object-contain" />
					{/if}
					<!-- Ripple canvas - only visible when ready -->
					<canvas
						bind:this={rippleCanvas}
						class="w-full h-full object-contain {rippleCanvasReady ? '' : 'hidden'}"
						width="640"
						height="640"
					></canvas>
					<!-- Clear button during generation -->
					<button
						on:click|stopPropagation={clearReference}
						class="absolute top-2 right-2 w-8 h-8 rounded-full bg-zinc-700 hover:brightness-125 text-white flex items-center justify-center"
						title="Clear reference"
					>
						<i class="fa-solid fa-xmark"></i>
					</button>
				{:else if referenceImage && !currentDisplayImage}
					<!-- Reference image before generation -->
					<img src={referenceImage} alt="Reference" class="w-full h-full object-contain" />
					<!-- Clear button -->
					<button
						on:click|stopPropagation={clearReference}
						class="absolute top-2 right-2 w-8 h-8 rounded-full bg-zinc-700 hover:brightness-125 text-white flex items-center justify-center"
						title="Clear reference"
					>
						<i class="fa-solid fa-xmark"></i>
					</button>
				{:else if currentDisplayImage}
					<!-- Generated/preview image (done state) -->
					<img
						src={currentDisplayImage}
						alt="Generated texture"
						class="w-full h-full object-contain"
					/>
				{:else if referenceImage}
					<!-- Fallback to reference image -->
					<img src={referenceImage} alt="Reference" class="w-full h-full object-contain" />
					<button
						on:click|stopPropagation={clearReference}
						class="absolute top-2 right-2 w-8 h-8 rounded-full bg-zinc-700 hover:brightness-125 text-white flex items-center justify-center"
						title="Clear reference"
					>
						<i class="fa-solid fa-xmark"></i>
					</button>
				{/if}
			</div>

			<!-- Timer - always present, visibility controlled to prevent layout shift -->
			<div
				class="mt-4 h-5 text-sm text-zinc-600 dark:text-zinc-400 {toolState === 'generating'
					? 'visible'
					: 'invisible'}"
			>
				{#if estimatedWaitTime}
					{elapsedTime.toFixed(1)}s / {estimatedWaitTime.toFixed(1)}s
				{:else}
					{elapsedTime.toFixed(1)}s
				{/if}
			</div>

			<!-- Error message -->
			{#if errorMessage}
				<p class="mt-2 text-sm text-red-500 dark:text-red-400 text-center max-w-md">
					{errorMessage}
				</p>
			{/if}

			<!-- Action buttons - fixed height container to prevent layout shift -->
			<div class="mt-4 h-[140px] flex flex-col items-center">
				{#if toolState === 'done'}
					<!-- Result action buttons -->
					<div class="flex flex-col gap-2 w-64">
						<button
							on:click={downloadTexture}
							class="w-full py-2 rounded shadow-md text-sm font-medium border
							bg-primary-50 border-primary-200 hover:brightness-90 text-zinc-900
							dark:bg-zinc-900 dark:border-zinc-800 dark:hover:brightness-150 dark:text-zinc-300"
						>
							<i class="fa-solid fa-download mr-2"></i>
							Download texture as .png
						</button>
						<button
							on:click={openInMaterialGenerator}
							class="w-full py-2 rounded shadow-md text-sm font-medium border
							bg-primary-50 border-primary-200 hover:brightness-90 text-zinc-900
							dark:bg-zinc-900 dark:border-zinc-800 dark:hover:brightness-150 dark:text-zinc-300"
						>
							<i class="fa-solid fa-cube mr-2"></i>
							Create material from texture
						</button>
						<button
							on:click={useToolAgain}
							class="w-full py-2 rounded shadow-md text-sm font-medium border
							bg-primary-50 border-primary-200 hover:brightness-90 text-zinc-900
							dark:bg-zinc-900 dark:border-zinc-800 dark:hover:brightness-150 dark:text-zinc-300"
						>
							<i class="fa-solid fa-rotate-left mr-2"></i>
							Use tool again
						</button>
					</div>
				{:else}
					<!-- Generate button - hidden when generating -->
					{#if toolState !== 'generating'}
						<button
							on:click={generateTexture}
							disabled={toolState === 'upload'}
							class="w-64 py-2 rounded shadow-md text-sm font-medium border
							bg-zinc-900 border-zinc-800 text-zinc-100
							dark:bg-zinc-100 dark:border-zinc-200 dark:text-zinc-900
							{toolState === 'upload'
								? 'opacity-50 cursor-not-allowed'
								: 'hover:brightness-90 dark:hover:brightness-125'}"
						>
							Generate texture from reference
						</button>
					{/if}
					{#if lastSuccessfulTexture && toolState === 'ready'}
						<button
							on:click={backToResult}
							class="w-64 py-2 mt-2 rounded shadow-md text-sm font-medium border
							bg-primary-50 border-primary-200 hover:brightness-90 text-zinc-900
							dark:bg-zinc-900 dark:border-zinc-800 dark:hover:brightness-150 dark:text-zinc-300"
						>
							<i class="fa-solid fa-arrow-right mr-2"></i>
							Back to result
						</button>
					{/if}
				{/if}
			</div>
		</div>
	</div>
</div>

<LoginOrSignUpModal
	bind:this={loginOrSignUpModal}
	currentUser={$authStore.user ?? undefined}
	whichModal="sign-up"
/>
<UsageExceededModal
	bind:this={usageExceededModal}
	{usageExceededErrorDetail}
	generationTool="referenceToTexture"
/>

<style>
	:global(body) {
		margin: 0;
		padding: 0;
		overflow-x: hidden;
		overflow-y: auto;
	}
</style>
